import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError, formatDoctorName } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Plus, X, Save, Calendar, Clock, User } from "lucide-react";

const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const EMPTY_SLOT = { start: "09:00", end: "13:00", max_appts: 10 };

export default function Schedule() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [schedule, setSchedule] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [myDoctorId, setMyDoctorId] = useState(null);

  const isAdmin = user?.role === "admin";
  const isDoctor = user?.role === "doctor";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/doctors");
        setDoctors(data);
        if (isDoctor) {
          const me = data.find(d => d.email === user.email);
          if (me) {
            setMyDoctorId(me.id);
            setSelectedDoctorId(me.id);
          }
        } else if (data.length > 0) {
          setSelectedDoctorId(data[0].id);
        }
      } catch (err) {
        toast({ title: "Error", description: formatError(err), variant: "destructive" });
      } finally { setLoading(false); }
    })();
  }, [isDoctor, toast, user]);

  useEffect(() => {
    if (!selectedDoctorId) return;
    api.get(`/doctors/${selectedDoctorId}/schedule`)
      .then(r => setSchedule(r.data.weekly_schedule || {}))
      .catch(err => toast({ title: "Error", description: formatError(err), variant: "destructive" }));
  }, [selectedDoctorId, toast]);

  const addSlot = (day) => {
    setSchedule({ ...schedule, [day]: [...(schedule[day] || []), { ...EMPTY_SLOT }] });
  };
  const removeSlot = (day, idx) => {
    setSchedule({ ...schedule, [day]: schedule[day].filter((_, i) => i !== idx) });
  };
  const updateSlot = (day, idx, field, value) => {
    const slots = [...schedule[day]];
    slots[idx] = { ...slots[idx], [field]: field === "max_appts" ? parseInt(value) || 0 : value };
    setSchedule({ ...schedule, [day]: slots });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/doctors/${selectedDoctorId}/schedule`, { weekly_schedule: schedule });
      toast({ title: "Schedule saved successfully" });
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setSaving(false); }
  };

  const copyToAllWeekdays = (day) => {
    const slots = schedule[day] || [];
    const copy = { ...schedule };
    ["mon", "tue", "wed", "thu", "fri"].forEach(d => {
      copy[d] = JSON.parse(JSON.stringify(slots));
    });
    setSchedule(copy);
    toast({ title: "Copied to Mon–Fri" });
  };

  const clearDay = (day) => setSchedule({ ...schedule, [day]: [] });

  const totalSlots = DAYS.reduce((sum, d) => sum + (schedule[d.key]?.length || 0), 0);
  const totalCapacity = DAYS.reduce((sum, d) => sum + (schedule[d.key] || []).reduce((s, slot) => s + (slot.max_appts || 0), 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">Doctor Schedule Builder</h1>
          <p className="text-slate-500 text-sm mt-1">Configure weekly availability and appointment slots</p>
        </div>
        <Button onClick={handleSave} disabled={saving || !selectedDoctorId} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" data-testid="save-schedule-btn">
          <Save size={16} /> {saving ? "Saving..." : "Save Schedule"}
        </Button>
      </div>

      {/* Doctor Selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-slate-700">
            <User size={18} />
            <Label className="text-sm font-semibold">Doctor</Label>
          </div>
          {isDoctor && !isAdmin ? (
            <div className="text-sm bg-emerald-50 text-emerald-800 px-3 py-1.5 rounded-full font-medium">
              {formatDoctorName(doctors.find(d => d.id === myDoctorId)?.name || user?.name)}
            </div>
          ) : (
            <select
              value={selectedDoctorId}
              onChange={e => setSelectedDoctorId(e.target.value)}
              className="flex-1 max-w-sm border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              data-testid="schedule-doctor-select"
            >
              {doctors.map(d => <option key={d.id} value={d.id}>{formatDoctorName(d.name)} — {d.specialization}</option>)}
            </select>
          )}
          <div className="flex items-center gap-4 text-xs text-slate-500 ml-auto">
            <span><strong className="text-slate-900">{totalSlots}</strong> slots</span>
            <span><strong className="text-slate-900">{totalCapacity}</strong> weekly capacity</span>
          </div>
        </div>
      </div>

      {/* Weekly Grid */}
      <div className="grid grid-cols-1 gap-3">
        {DAYS.map(({ key, label }) => {
          const slots = schedule[key] || [];
          const dayTotal = slots.reduce((s, x) => s + (x.max_appts || 0), 0);
          return (
            <div key={key} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`schedule-day-${key}`}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50/50">
                <div className="flex items-center gap-3">
                  <Calendar size={16} className="text-emerald-600" />
                  <h3 className="font-semibold text-slate-900 text-sm">{label}</h3>
                  {slots.length > 0 && <span className="text-xs text-slate-500">· {slots.length} slot{slots.length > 1 ? "s" : ""} · {dayTotal} appts</span>}
                </div>
                <div className="flex items-center gap-1">
                  {slots.length > 0 && ["mon"].includes(key) && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500 hover:text-slate-900" onClick={() => copyToAllWeekdays(key)} data-testid={`copy-weekdays-${key}`}>
                      Copy to Mon-Fri
                    </Button>
                  )}
                  {slots.length > 0 && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-rose-500 hover:text-rose-700" onClick={() => clearDay(key)}>
                      Clear
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => addSlot(key)} data-testid={`add-slot-${key}`}>
                    <Plus size={12} /> Add Slot
                  </Button>
                </div>
              </div>
              <div className="p-4">
                {slots.length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-2">Off — no slots configured</p>
                ) : (
                  <div className="space-y-2">
                    {slots.map((slot, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2.5">
                        <Clock size={14} className="text-slate-400 flex-shrink-0" />
                        <div className="flex items-center gap-1.5">
                          <Input type="time" value={slot.start} onChange={e => updateSlot(key, idx, "start", e.target.value)} className="h-8 w-28 text-xs border-slate-200" data-testid={`slot-start-${key}-${idx}`} />
                          <span className="text-xs text-slate-400">to</span>
                          <Input type="time" value={slot.end} onChange={e => updateSlot(key, idx, "end", e.target.value)} className="h-8 w-28 text-xs border-slate-200" data-testid={`slot-end-${key}-${idx}`} />
                        </div>
                        <div className="flex items-center gap-1.5 ml-2">
                          <Label className="text-xs text-slate-500 whitespace-nowrap">Max</Label>
                          <Input type="number" min={0} value={slot.max_appts} onChange={e => updateSlot(key, idx, "max_appts", e.target.value)} className="h-8 w-16 text-xs border-slate-200" data-testid={`slot-max-${key}-${idx}`} />
                          <span className="text-xs text-slate-400">appts</span>
                        </div>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 ml-auto text-slate-400 hover:text-rose-600" onClick={() => removeSlot(key, idx)} data-testid={`remove-slot-${key}-${idx}`}>
                          <X size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
